import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Ticket, Star, Settings, CreditCard, Bell, Download, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { ticketsService } from '../services/ticketsService';
import { favoritesService } from '../services/favoritesService';
import { generateTicketPDF } from '../utils/ticketGenerator';
import { Button, LoadingSpinner, EmptyState, Card } from '../components/ui';
import TicketCard from '../components/TicketCard';
import Footer from '../components/Footer';
import { useToast } from '../contexts/ToastContext';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { openLogin } = useAuthModal();
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState<'tickets' | 'upcoming' | 'history' | 'favorites'>('tickets');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [tickets, setTickets] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [favoriteEvents, setFavoriteEvents] = useState<any[]>([]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadDashboardData();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [ticketsData, ordersData, favoritesData] = await Promise.all([
        ticketsService.getTickets().catch(() => []),
        ticketsService.getOrders().catch(() => []),
        favoritesService.getFavorites().catch(() => [])
      ]);
      
      setTickets(ticketsData);
      setOrders(ordersData);
      setFavoriteEvents(favoritesData);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Transform tickets for TicketCard component
  const transformedTickets = tickets.map(ticket => ({
    id: ticket.id,
    eventId: ticket.event.id,
    eventTitle: ticket.event.title,
    eventImage: ticket.event.image,
    date: ticket.event.date,
    time: ticket.event.time,
    venue: ticket.event.venue,
    seat: ticket.seatInfo,
    qrCode: ticket.qrCode,
    ticketType: ticket.ticketType,
    price: ticket.price,
    status: ticket.status
  }));

  // Get upcoming events (events with future dates from tickets)
  const upcomingEvents = tickets
    .filter(ticket => {
      const eventDate = new Date(ticket.event.date);
      return eventDate >= new Date() && ticket.status === 'valid';
    })
    .map(ticket => ({
      id: ticket.event.id,
      title: ticket.event.title,
      date: ticket.event.date,
      time: ticket.event.time,
      venue: ticket.event.venue,
      image: ticket.event.image
    }))
    .filter((event, index, self) => 
      index === self.findIndex(e => e.id === event.id)
    );

  const orderHistory = orders.map(order => ({
    id: order.id,
    orderNumber: order.orderNumber,
    date: new Date(order.createdAt).toLocaleDateString(),
    event: order.event.title,
    total: order.finalAmount,
    status: order.paymentStatus
  }));

  const tabs = [
    { key: 'tickets', label: t('dashboard.myTickets'), count: tickets.length },
    { key: 'upcoming', label: t('dashboard.upcomingEvents'), count: upcomingEvents.length },
    { key: 'history', label: t('dashboard.orderHistory'), count: orders.length },
    { key: 'favorites', label: t('dashboard.favorites'), count: favoriteEvents.length }
  ];

  const handleAddToWallet = async (ticketId: string) => {
    // TODO: Implement wallet integration
    alert(`${t('wallet.addToAppleWallet')} - Ticket ${ticketId}`);
  };

  const handleDownloadTicket = async (ticketId: string) => {
    try {
      const ticket = tickets.find(t => t.id === ticketId);
      if (!ticket) {
        alert('Ticket not found');
        return;
      }

      const ticketData = {
        ticketNumber: ticket.ticketNumber,
        eventTitle: ticket.event.title,
        eventDate: ticket.event.date,
        eventTime: ticket.event.time,
        eventLocation: ticket.event.venue,
        eventVenue: ticket.event.venue,
        category: ticket.event.category,
        ticketType: ticket.ticketType,
        price: ticket.price,
        customerName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : 'Guest',
        customerPhone: user?.phone || '',
        seatInfo: ticket.seatInfo,
        qrCode: ticket.qrCode
      };

      const pdfBlob = await generateTicketPDF(ticketData);
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-${ticket.ticketNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      success('Ticket downloaded successfully!');
    } catch (err: any) {
      console.error('Error downloading ticket:', err);
      showError('Failed to download ticket. Please try again.');
    }
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{background: 'var(--liquid-gradient-bg)'}}>
      {/* Enhanced Liquid Glass Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave" style={{animationDelay: '4s'}}></div>
        <div className="absolute top-20 right-1/4 w-48 h-48 liquid-glass opacity-8 liquid-animate-pulse" style={{animationDelay: '6s'}}></div>
        <div className="absolute bottom-40 left-1/4 w-72 h-72 liquid-glass opacity-12 liquid-animate-morph" style={{animationDelay: '8s'}}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 liquid-glass-strong py-8 pt-24 pb-20" style={{color: 'var(--liquid-text-primary)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">
                {t('dashboard.welcome')}, {user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'Guest'}!
              </h1>
              <p className="opacity-80">
                {isLoading ? 'Loading...' : `You have ${upcomingEvents.length} upcoming events`}
              </p>
            </div>
            
            <div className="hidden md:flex space-x-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{tickets.length}</div>
                <div className="opacity-70 text-sm">Total Tickets</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{upcomingEvents.length}</div>
                <div className="opacity-70 text-sm">Upcoming</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Button variant="outline" className="p-4 h-auto flex-col space-y-2">
            <Settings size={24} />
            <span className="text-sm">{t('dashboard.accountSettings')}</span>
          </Button>
          <Button variant="outline" className="p-4 h-auto flex-col space-y-2">
            <CreditCard size={24} />
            <span className="text-sm">{t('dashboard.paymentMethods')}</span>
          </Button>
          <Button variant="outline" className="p-4 h-auto flex-col space-y-2">
            <Bell size={24} />
            <span className="text-sm">{t('dashboard.notifications')}</span>
          </Button>
          <Button variant="outline" className="p-4 h-auto flex-col space-y-2">
            <Download size={24} />
            <span className="text-sm">{t('dashboard.help')}</span>
          </Button>
        </div>

        {/* Tab Navigation */}
        <Card variant="glass" className="p-6">
          {isLoading ? (
            <LoadingSpinner size="lg" text="Loading your dashboard..." />
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-400 mb-4">{error}</p>
              <Button onClick={loadDashboardData} variant="primary">Try Again</Button>
            </div>
          ) : !isAuthenticated ? (
            <EmptyState
              icon={Ticket}
              title="Sign in required"
              description="Please sign in to view your dashboard"
              action={{
                label: "Sign In",
                onClick: openLogin
              }}
            />
          ) : (
            <>
              <div className="flex space-x-1 glass p-1 rounded-xl mb-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      activeTab === tab.key
                        ? 'bg-white/20 text-white shadow-sm'
                        : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{color: 'var(--liquid-text-primary)'}}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'tickets' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                      {t('dashboard.myTickets')}
                    </h2>
                    {transformedTickets.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => {
                        transformedTickets.forEach(ticket => handleDownloadTicket(ticket.id));
                      }}>
                        <Download size={16} className="mr-2" />
                        Download All
                      </Button>
                    )}
                  </div>
                  
                  {transformedTickets.length > 0 ? (
                    <div className="grid gap-4">
                      {transformedTickets.map((ticket) => (
                        <div key={ticket.id} className="glass rounded-xl p-4">
                          <TicketCard ticket={ticket} />
                          
                          <div className="flex space-x-2 mt-4 pt-4 border-t border-white/10">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadTicket(ticket.id)}
                            >
                              <Download size={16} className="mr-2" />
                              Download
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddToWallet(ticket.id)}
                            >
                              <Wallet size={16} className="mr-2" />
                              Add to Wallet
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={Ticket}
                      title="No tickets yet"
                      description="Purchase tickets to events to see them here"
                      action={{
                        label: "Browse Events",
                        onClick: () => navigate('/')
                      }}
                    />
                  )}
                </div>
              )}

              {activeTab === 'upcoming' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('dashboard.upcomingEvents')}
                  </h2>
                  
                  {upcomingEvents.length > 0 ? (
                    <div className="grid gap-4">
                      {upcomingEvents.map((event) => (
                        <div key={event.id} className="flex items-center space-x-4 p-4 glass rounded-xl">
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                              {event.title}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm opacity-70" style={{color: 'var(--liquid-text-muted)'}}>
                              <div className="flex items-center space-x-1">
                                <Calendar size={14} />
                                <span>{event.date}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock size={14} />
                                <span>{event.time}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <MapPin size={14} />
                                <span>{event.venue}</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/event/${event.id}`)}>
                            <Ticket size={16} className="mr-2" />
                            View Event
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={Calendar}
                      title="No upcoming events"
                      description="You don't have any upcoming events"
                      action={{
                        label: "Browse Events",
                        onClick: () => navigate('/')
                      }}
                    />
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('dashboard.orderHistory')}
                  </h2>
                  
                  {orderHistory.length > 0 ? (
                    <div className="space-y-3">
                      {orderHistory.map((order) => (
                        <div key={order.id} className="flex justify-between items-center p-4 glass rounded-xl">
                          <div>
                            <h3 className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                              {order.event}
                            </h3>
                            <p className="text-sm opacity-70" style={{color: 'var(--liquid-text-muted)'}}>
                              Order #{order.orderNumber} • {order.date}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                              ${order.total.toFixed(2)}
                            </div>
                            <div className={`text-sm ${
                              order.status === 'completed' ? 'text-green-400' : 
                              order.status === 'pending' ? 'text-yellow-400' : 
                              'text-blue-400'
                            }`}>
                              {order.status}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={CreditCard}
                      title="No order history"
                      description="Your past orders will appear here"
                      action={{
                        label: "Browse Events",
                        onClick: () => navigate('/')
                      }}
                    />
                  )}
                </div>
              )}

              {activeTab === 'favorites' && (
                <div className="space-y-4">
                  <h2 className="text-xl font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                    {t('dashboard.favorites')}
                  </h2>
                  
                  {favoriteEvents.length > 0 ? (
                    <div className="grid gap-4">
                      {favoriteEvents.map((event) => (
                        <div key={event.id} className="flex items-center space-x-4 p-4 glass rounded-xl">
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold" style={{color: 'var(--liquid-text-primary)'}}>
                              {event.title}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm opacity-70" style={{color: 'var(--liquid-text-muted)'}}>
                              <div className="flex items-center space-x-1">
                                <Calendar size={14} />
                                <span>{event.date}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <MapPin size={14} />
                                <span>{event.venue || 'TBA'}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Star size={14} />
                                <span>${event.price}</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="primary" size="sm" onClick={() => navigate(`/event/${event.id}`)}>
                            Get Tickets
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon={Star}
                      title="No favorites yet"
                      description="Save events you like to see them here"
                      action={{
                        label: "Browse Events",
                        onClick: () => navigate('/')
                      }}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
